/**
 * @fileoverview Send payment details dialog for a My Page target.
 * @description Opened from the target's details when the target is at Subscription Requested and
 * the backend lists Send Payment Details among the allowed actions. The user picks which bank
 * contact to email (a contact with no address cannot be chosen) and the backend emails the
 * organisation's legal and bank details to it, marking the target Awaiting Payment. A refusal (the
 * organisation details not yet saved, a contact without an address) shows as the dialog's error. The
 * parent mounts it only while it is open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSendMyPagePaymentDetails } from '@/features/sales/hooks/useSendMyPagePaymentDetails'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'

import type {
  MyPagePaymentRecipient,
  MyPageTargetContact,
  MyPageTargetDetails,
} from '@/features/sales/types/my-page.types'

interface MyPageSendPaymentDialogProps {
  target: MyPageTargetDetails
  onClose: () => void
}

/**
 * @description One recipient choice: the contact's name and email, or a note that it has no email.
 * @param {MyPagePaymentRecipient} kind - Which contact.
 * @param {MyPageTargetContact | null} contact - The contact, or null when none is held.
 * @param {(key: string) => string} translate - The translate function.
 * @returns {{ value: string; label: string; disabled: boolean }} The select option.
 */
function recipientOption(
  kind: MyPagePaymentRecipient,
  contact: MyPageTargetContact | null,
  translate: (key: string) => string,
) {
  const kindLabel = translate(`sales.myWork.dialogs.sendPayment.${kind}`)
  if (!contact) {
    return {
      value: kind,
      label: `${kindLabel} — ${translate('sales.myWork.dialogs.sendPayment.noContact')}`,
      disabled: true,
    }
  }
  if (!contact.email) {
    return {
      value: kind,
      label: `${kindLabel} — ${translate('sales.myWork.dialogs.sendPayment.noEmail')}`,
      disabled: true,
    }
  }
  return {
    value: kind,
    label: `${kindLabel} — ${contact.name} (${contact.email})`,
    disabled: false,
  }
}

/**
 * @component
 * @param {MyPageSendPaymentDialogProps} props - The target and the close handler.
 * @param {MyPageTargetDetails} props.target - The target as the details last showed it.
 * @param {() => void} props.onClose - Called when the dialog is dismissed or the details are sent.
 */
function MyPageSendPaymentDialog({ target, onClose }: MyPageSendPaymentDialogProps) {
  const { t } = useTranslation()
  const send = useSendMyPagePaymentDetails(onClose)
  const close = useGuardedClose(onClose, send.isPending)
  const options = [
    recipientOption('PRIMARY', target.contacts.primary, t),
    recipientOption('SECONDARY', target.contacts.secondary, t),
  ]
  const firstUsable = options.find((option) => !option.disabled)
  const [recipient, setRecipient] = useState<MyPagePaymentRecipient>(
    firstUsable?.value ?? 'PRIMARY',
  )

  /**
   * @description Emails the payment details to the chosen contact.
   */
  const onConfirm = () => {
    send.mutate({ targetId: target.targetId, recipient })
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="my-page-send-payment-dialog"
      title={t('sales.myWork.dialogs.sendPayment.title')}
      subtitle={target.bankName}
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13.5px] text-ink">
          {t('sales.myWork.dialogs.sendPayment.description')}
        </p>
        <SelectField
          id="myPagePaymentRecipient"
          data-testid="my-page-send-payment-recipient"
          label={t('sales.myWork.dialogs.sendPayment.recipientLabel')}
          value={recipient}
          onChange={(event) => setRecipient(event.target.value as MyPagePaymentRecipient)}
          options={options}
        />
        {send.isError && <SalesRequestError error={send.error} />}
        <DialogActions
          testIdPrefix="my-page-send-payment"
          cancelLabel={t('sales.myWork.dialogs.cancel')}
          confirmLabel={t('sales.myWork.dialogs.sendPayment.submit')}
          onCancel={close}
          onConfirm={onConfirm}
          isLoading={send.isPending}
          disabled={firstUsable === undefined}
        />
      </div>
    </Modal>
  )
}

export default MyPageSendPaymentDialog
